import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  globalInfo,
  globalMenuInfo,
  activeMenuInfo,
  userInfo,
  globalFooterMsg,
  globalSearch,
  ChatAddRowRequest,
  ChatAddRowCallResult,
  ChatScreenData,
  ChatScreenGridData,
  ChatScreenDataProviderHandle,
  onSearchInfo,
  ChatSearchFormData,
  requestControllerState,
  loadingState,
  ChatCopyProgressState,
  MenuChangeState,
  Menu,
  MenuStore,
  SnEditorState,
  NavigationStore,
} from "@shared/type/CommonType";
import { ShareStoreState } from "@shared/type/ShareType";
import { aiSignalFlag } from "@shared/libs/aiSignalFlag";

export const global = create(
  persist<globalInfo>(
    (set) => ({
      userInfo: {},
      setUserInfo: (newState: userInfo) => {
        set(() => ({ userInfo: newState }));
      },

      config: {},
      setConfig: (newState: any) => {
        set(() => ({ config: newState }));
      },

      bookmark: {},
      setBookmark: (newState: any) => {
        set(() => ({ bookmark: newState }));
      },
    }),
    {
      name: "G2Global-key",
    }
  )
);

export const globalMenu = create<globalMenuInfo>((set) => ({
  activeMenuInfo: { cd_menu: "" },
  setActiveMenuInfo: (newState: activeMenuInfo) => {
    // 같은 메뉴면 쓰지 않는다.
    // 매번 새 객체를 넣으면 globalMenu를 구독하는 모든 컴포넌트(Footer, 열려 있는
    // 모든 탭의 SnResizable 등)가 값이 그대로인데도 전부 리렌더된다.
    set((state) => {
      const prev = state.activeMenuInfo;
      if (
        prev &&
        prev.cd_menu === newState.cd_menu &&
        prev.nm_menu === newState.nm_menu &&
        prev.cd_module === newState.cd_module
      ) {
        return state;
      }
      return { activeMenuInfo: newState };
    });
  },
  chatScreenSelections: {},
  setChatScreenSelection: (cd_menu, selection) => {
    if (!cd_menu) return;
    set((state) => ({
      chatScreenSelections: {
        ...state.chatScreenSelections,
        [cd_menu]: selection,
      },
    }));
  },
  clearChatScreenSelection: (cd_menu) => {
    if (!cd_menu) return;
    set((state) => {
      const { [cd_menu]: _removed, ...chatScreenSelections } =
        state.chatScreenSelections;
      return { chatScreenSelections };
    });
  },
}));

export const footerMsgStore = create<globalFooterMsg>((set) => ({
  footerMsg: new Map(),
  handleFooterMsg: (cd_menu, message) =>
    set((prev) => {
      // 같은 메시지면 쓰지 않는다.
      // callApi는 성공/실패와 무관하게 finally에서 항상 이걸 호출하는데,
      // 매번 새 Map을 넣으면 API 한 번당 MainLayout·Footer가 무조건 다시 그려진다.
      // (메뉴를 여러 개 동시에 열면 그 리렌더가 한꺼번에 몰린다)
      if (prev.footerMsg.get(cd_menu) === message) return prev;
      const newMap = new Map(prev.footerMsg);
      newMap.set(cd_menu, message);
      return { footerMsg: newMap };
    }),
}));

// 단위화면 초기화(getInitForm/getInitFormData/getInitSheet) 진행 상태.
interface menuInitState {
  initializingMenus: Record<string, number>;
  beginMenuInit: (cd_menu: string) => void;
  endMenuInit: (cd_menu: string) => void;
}

export const menuInitStore = create<menuInitState>((set) => ({
  initializingMenus: {},
  beginMenuInit: (cd_menu) => {
    if (!cd_menu) return;
    set((state) => ({
      initializingMenus: {
        ...state.initializingMenus,
        [cd_menu]: (state.initializingMenus[cd_menu] || 0) + 1,
      },
    }));
  },
  endMenuInit: (cd_menu) => {
    if (!cd_menu) return;
    set((state) => {
      const count = (state.initializingMenus[cd_menu] || 0) - 1;
      if (count <= 0) {
        const { [cd_menu]: _removed, ...initializingMenus } =
          state.initializingMenus;
        return { initializingMenus };
      }
      return {
        initializingMenus: { ...state.initializingMenus, [cd_menu]: count },
      };
    });
  },
}));

/** 메뉴 초기화 API(getInitForm, getInitFormData, getInitSheet) 호출을 감싸 시작/종료를 menuInitStore에 기록 */
export const trackMenuInit = async <T>(
  cd_menu: string,
  task: () => Promise<T>
): Promise<T> => {
  menuInitStore.getState().beginMenuInit(cd_menu);
  try {
    return await task();
  } finally {
    menuInitStore.getState().endMenuInit(cd_menu);
  }
};

export const onSearchStore = create<globalSearch>((set, get) => ({
  onSearchInfo: {},
  chatSearchPanels: {},
  chatScreenDataProviders: {},
  chatScreenGridDataProviders: {},
  setOnSearch: (newState: onSearchInfo) => {
    set(() => ({ onSearchInfo: newState }));
  },
  runOnSearch: async (targetMenuCode?: string) => {
    const { componentRefs, index, menuData, isLoading, tabTitle } =
      get().onSearchInfo;
    const targetIndex = getSearchMenuIndex(get().onSearchInfo, targetMenuCode);

    if (
      componentRefs &&
      targetIndex !== undefined &&
      typeof componentRefs[targetIndex]?.current?.onSearch === "function"
    ) {
      isLoading?.(menuData?.[targetIndex]?.cd_menu, true);
      tabTitle?.(menuData?.[targetIndex]?.cd_menu, true);
      try {
        await componentRefs[targetIndex].current.onSearch();
        return true;
      } finally {
        isLoading?.(menuData?.[targetIndex]?.cd_menu, false);
        tabTitle?.(menuData?.[targetIndex]?.cd_menu, false);
      }
    }
    return false;
  },
  registerSearchPanel: (cd_menu, handle) => {
    if (!cd_menu) return;
    set((state) => ({
      chatSearchPanels: {
        ...state.chatSearchPanels,
        [cd_menu]: handle,
      },
    }));
  },
  unregisterSearchPanel: (cd_menu, handle) => {
    if (!cd_menu) return;
    /* 다른 조회패널이 맡긴 것은 지우지 않는다 (2026-10-16) */
    if (handle && get().chatSearchPanels[cd_menu] !== handle) return;
    set((state) => {
      const { [cd_menu]: _removed, ...chatSearchPanels } =
        state.chatSearchPanels;
      return { chatSearchPanels };
    });
  },
  registerChatScreenDataProvider: (cd_menu, handle) => {
    if (!cd_menu) return;
    set((state) => ({
      chatScreenDataProviders: {
        ...state.chatScreenDataProviders,
        [cd_menu]: handle,
      },
    }));
  },
  unregisterChatScreenDataProvider: (cd_menu) => {
    if (!cd_menu) return;
    set((state) => {
      const { [cd_menu]: _removed, ...chatScreenDataProviders } =
        state.chatScreenDataProviders;
      return { chatScreenDataProviders };
    });
  },
  registerChatScreenGridDataProvider: (cd_menu, grid_key, handle) => {
    if (!cd_menu || !grid_key) return;
    set((state) => ({
      chatScreenGridDataProviders: {
        ...state.chatScreenGridDataProviders,
        [cd_menu]: {
          ...(state.chatScreenGridDataProviders[cd_menu] || {}),
          [grid_key]: handle,
        },
      },
    }));
  },
  unregisterChatScreenGridDataProvider: (cd_menu, grid_key) => {
    if (!cd_menu || !grid_key) return;
    set((state) => {
      const currentProviders = state.chatScreenGridDataProviders[cd_menu] || {};
      const { [grid_key]: _removed, ...nextMenuProviders } = currentProviders;
      const { [cd_menu]: _removedMenu, ...nextProviders } =
        state.chatScreenGridDataProviders;

      if (Object.keys(nextMenuProviders).length === 0) {
        return { chatScreenGridDataProviders: nextProviders };
      }

      return {
        chatScreenGridDataProviders: {
          ...nextProviders,
          [cd_menu]: nextMenuProviders,
        },
      };
    });
  },
  getCurrentSearchFormData: () => {
    const menuCode = getActiveSearchMenuCode(get().onSearchInfo);
    if (!menuCode) return undefined;
    return get().chatSearchPanels[menuCode]?.getFormdata?.();
  },
  // 챗봇 행추가 · 행복사 — 화면의 표준 처리(onInsert · 그리드 [+])로 (2026-10-15). 화면별 챗봇 함수(onChatAddRow)는 걷어냈다.
  // 툴바 [행추가](onInsert)가 있거나 그리드에 [+]가 있으면 지원한다. 새 그리드라는 것만으로는 아니다 —
  // 현황처럼 조회만 하는 메뉴가 "된다" 로 알려져 AI 가 행추가를 시키고 실패했다 (2026-10-16).
  supportsChatAddRow: () => {
    const ref = activeScreenRef(get().onSearchInfo);
    if (typeof ref?.onInsert === "function") return true;
    const menuCode = getActiveSearchMenuCode(get().onSearchInfo);
    const grids = menuCode ? get().chatScreenGridDataProviders[menuCode] : undefined;
    return Object.values(grids || {}).some((g) => !!g?.chatActions?.canAdd());
  },
  /* 화면이 선언하던 행 식별 키는 없어졌다 — 기준 건 지정은 AI 가 화면 값에서 고른 칸으로 한다 */
  chatAddRowKeyField: () => undefined,
  callChatAddRow: async (
    request: ChatAddRowRequest,
    targetMenuCode?: string
  ): Promise<ChatAddRowCallResult> => {
    // 응답 대기 중 탭 전환 방어 — 응답의 메뉴가 현재 활성 메뉴와 다르면 실행하지 않는다.
    const activeMenuCode = getActiveSearchMenuCode(get().onSearchInfo);
    if (targetMenuCode && targetMenuCode !== activeMenuCode) {
      return { status: "MENU_MISMATCH" };
    }
    if (!get().supportsChatAddRow()) {
      return { status: "UNSUPPORTED" };
    }
    const ref = activeScreenRef(get().onSearchInfo);
    const grids = activeMenuCode ? get().chatScreenGridDataProviders[activeMenuCode] : undefined;
    /* 쓸 때 불러온다 — 실행부가 코드박스(이름→코드)를, 코드박스가 이 스토어를 불러 고리가 생긴다 */
    const { runChatAddRow } = await import("@shared/utils/ChatAddRowUtils");
    /* 하단 줄을 넣을 때마다 그 메뉴 화면 덮개에 "N / 전체건" — 끝나면(실패해도) 걷는다 */
    const progress = chatCopyProgressStore.getState();
    try {
      const result = await runChatAddRow(request, ref, grids, (done, total) => {
        if (activeMenuCode) progress.setProgress(activeMenuCode, done, total);
      });
      return { status: "EXECUTED", result };
    } finally {
      if (activeMenuCode) progress.clearProgress(activeMenuCode);
    }
  },
  getCurrentChatScreenData: () => {
    const menuCode = getActiveSearchMenuCode(get().onSearchInfo);
    const provider = menuCode
      ? get().chatScreenDataProviders[menuCode]
      : undefined;
    if (provider) {
      return provider.getScreenData();
    }

    const gridData = menuCode
      ? getChatScreenDataFromGridProviders(
          get().chatScreenGridDataProviders[menuCode]
        )
      : undefined;
    if (gridData) {
      return gridData;
    }

    return undefined;
  },
  applySearchParamsAndRun: async (
    paramsPatch: ChatSearchFormData,
    targetMenuCode?: string,
    options?: { reset?: boolean; run?: boolean }
  ) => {
    const menuCode = getSearchMenuCode(get().onSearchInfo, targetMenuCode);
    const patchEntries = Object.entries(paramsPatch || {});
    if (targetMenuCode && !menuCode) {
      return {
        applied: false,
        ignoredParams: patchEntries.map(([key]) => key),
        reason: "MENU_MISMATCH",
      };
    }
    const handle = menuCode ? get().chatSearchPanels[menuCode] : undefined;
    if (!handle) {
      /* 넣을 조건도 없고 초기화도 아니면 조회패널 없이 조회 — 툴바 [조회]와 같다.
         조회조건이 없는 화면에서도 "조회해줘" 가 된다 (2026-10-16) */
      if (patchEntries.length === 0 && !options?.reset) {
        if (options?.run === false) return { applied: true, ignoredParams: [] };
        aiSignalFlag.begin();
        let ran = false;
        try {
          ran = await get().runOnSearch(menuCode);
        } finally {
          aiSignalFlag.end();
        }
        return ran
          ? { applied: true, ignoredParams: [] }
          : { applied: false, ignoredParams: [], reason: "SEARCH_NOT_RUN" };
      }
      /* 조건을 넣거나 초기화해야 하는데 조회조건이 없는 메뉴 */
      return {
        applied: false,
        ignoredParams: patchEntries.map(([key]) => key),
        reason: "NO_ACTIVE_SEARCH_PANEL",
      };
    }

    /* 조회조건 초기화 — 조회패널의 ↻ 와 같은 처리를 먼저. 화면이 초기화 버튼을 숨겼으면 하지 않는다 (2026-10-16) */
    if (options?.reset) {
      if (typeof handle.resetFormdata !== "function") {
        return {
          applied: false,
          ignoredParams: patchEntries.map(([key]) => key),
          reason: "RESET_UNSUPPORTED",
        };
      }
      handle.resetFormdata();
    }

    const allowedFieldNames = new Set(handle.getAllowedFieldNames?.() || []);
    const allowedPatch: ChatSearchFormData = {};
    const ignoredParams: string[] = [];
    for (const [key, value] of patchEntries) {
      if (allowedFieldNames.has(key)) {
        allowedPatch[key] = value;
      } else {
        ignoredParams.push(key);
      }
    }

    if (patchEntries.length > 0 && Object.keys(allowedPatch).length === 0) {
      return {
        applied: false,
        ignoredParams,
        reason: "NO_ALLOWED_PARAMS",
      };
    }

    if (Object.keys(allowedPatch).length > 0) {
      /* 코드 칸 · 콤보 칸은 사람이 칠 때와 같이 찾아 채운다. 고르지 않으면 조회하지 않는다 (2026-10-02).
         SnCodeBox 가 이 파일을 읽으므로 순환을 피해 늦게 읽는다 */
      const { resolveChatFieldValues } = await import("@shared/libs/aiFieldResolve");
      /* 찾는 조회도 AI 가 돌리는 것 — 사람 조회로 세지 않게 */
      aiSignalFlag.begin();
      let resolved: Awaited<ReturnType<typeof resolveChatFieldValues>>;
      try {
        resolved = await resolveChatFieldValues(handle, allowedPatch);
      } finally {
        aiSignalFlag.end();
      }
      if (resolved.fail) {
        return {
          applied: false,
          ignoredParams,
          reason: resolved.fail.reason,
          message: resolved.fail.message,
          /* 고른 뒤에도 같은 조회 여부로 — 넣기만이면 고른 뒤에도 조회하지 않는다 (2026-10-16) */
          pick: resolved.fail.pick ? { ...resolved.fail.pick, menuCode, run: options?.run } : undefined,
        };
      }
      handle.setFormdata({
        ...(handle.getFormdata?.() || {}),
        ...resolved.patch,
      });
    }
    /* 넣기만("○○로 세팅해줘" · "초기화해줘") — 조회는 사람이 누른다 */
    if (options?.run === false) {
      return { applied: true, ignoredParams };
    }
    // AI 가 돌리는 조회다. fnGetDataSet 이 사람 조회로 세지 않게 표시한다(행동 신호, D44)
    aiSignalFlag.begin();
    let searchRun = false;
    try {
      searchRun = await get().runOnSearch(menuCode);
    } finally {
      aiSignalFlag.end();
    }
    return searchRun
      ? { applied: true, ignoredParams }
      : { applied: false, ignoredParams, reason: "SEARCH_NOT_RUN" };
  },
}));

/**
 * 메뉴 화면에서 직접 공통 스피너를 조작하는 공통 헬퍼
 * 예외가 나도 스피너는 반드시 종료
 */
export const withMenuLoading = async <T>(
  cd_menu: string,
  task: () => Promise<T> | T
): Promise<T> => {
  const { isLoading, tabTitle } = onSearchStore.getState().onSearchInfo;
  isLoading?.(cd_menu, true);
  tabTitle?.(cd_menu, true);
  try {
    return await task();
  } finally {
    isLoading?.(cd_menu, false);
    tabTitle?.(cd_menu, false);
  }
};

function getChatScreenDataFromGridProviders(
  providers:
    | Record<string, ChatScreenDataProviderHandle | undefined>
    | undefined
): ChatScreenData | undefined {
  if (!providers) return undefined;

  let selectedRows: ChatScreenData["selectedRows"];
  let visibleRows: ChatScreenData["visibleRows"];
  let visibleColumns: ChatScreenData["visibleColumns"];
  let rowCount: number | undefined; // 상위 visibleRows를 준 그리드의 참 건수(캡 이전)
  const grids: Record<string, ChatScreenGridData> = {};

  for (const [gridId, provider] of Object.entries(providers).sort(
    ([left], [right]) => left.localeCompare(right)
  )) {
    const screenData = provider?.getScreenData();
    if (screenData?.grids) {
      Object.entries(screenData.grids).forEach(([nestedGridId, gridData]) => {
        if (gridData) {
          grids[nestedGridId] = gridData;
        }
      });
    } else if (screenData) {
      grids[gridId] = {
        ...(screenData.rows ? { rows: screenData.rows } : {}),
        ...(screenData.selectedRows
          ? { selectedRows: screenData.selectedRows }
          : {}),
        ...(screenData.visibleRows
          ? { visibleRows: screenData.visibleRows }
          : {}),
        ...(screenData.visibleColumns
          ? { visibleColumns: screenData.visibleColumns }
          : {}),
        ...(screenData.filters ? { filters: screenData.filters } : {}),
        ...(screenData.rowCount !== undefined
          ? { rowCount: screenData.rowCount }
          : {}),
        /* rows 와 같은 범위의 캡 이전 건수 — 보고서 부분 집계 판정용 (2026-10-16) */
        ...(screenData.rowsTotal !== undefined
          ? { rowsTotal: screenData.rowsTotal }
          : {}),
      };
    }
    if (!selectedRows && screenData?.selectedRows?.length) {
      selectedRows = screenData.selectedRows;
    }
    if (!visibleRows && screenData?.visibleRows?.length) {
      visibleRows = screenData.visibleRows;
      // 상위 visibleRows를 준 그리드의 참 건수도 함께(건수 답변용).
      if (screenData.rowCount !== undefined) rowCount = screenData.rowCount;
    }
    if (!visibleColumns && screenData?.visibleColumns?.length) {
      visibleColumns = screenData.visibleColumns;
    }
  }

  const hasGrids = Object.keys(grids).length > 0;
  if (!selectedRows && !visibleRows && !hasGrids) return undefined;

  return {
    ...(selectedRows ? { selectedRows } : {}),
    ...(visibleRows ? { visibleRows } : {}),
    ...(visibleColumns ? { visibleColumns } : {}),
    ...(rowCount !== undefined ? { rowCount } : {}),
    ...(hasGrids ? { grids } : {}),
  };
}

function getSearchMenuCode(
  onSearchInfo: onSearchInfo,
  targetMenuCode?: string
): string | undefined {
  const targetIndex = getSearchMenuIndex(onSearchInfo, targetMenuCode);
  if (targetIndex !== undefined) {
    const menuCode = onSearchInfo.menuData?.[targetIndex]?.cd_menu;
    if (menuCode) return menuCode;
  }
  const activeMenuCode = getActiveSearchMenuCode(onSearchInfo);
  if (!targetMenuCode || activeMenuCode === targetMenuCode) {
    return activeMenuCode;
  }
  return undefined;
}

function getSearchMenuIndex(
  onSearchInfo: onSearchInfo,
  targetMenuCode?: string
): number | undefined {
  const { index, menuData } = onSearchInfo;
  if (targetMenuCode && menuData) {
    const targetIndex = menuData.findIndex(
      (menu) => menu?.cd_menu === targetMenuCode
    );
    if (targetIndex >= 0) {
      return targetIndex;
    }
  }
  return typeof index === "number" ? index : undefined;
}

/** 활성 화면의 imperativeHandle(ref.current) — 툴바가 onInsert/onSave를 부르는 그 ref. */
function activeScreenRef(
  onSearchInfo: onSearchInfo
): { onInsert?: () => unknown } | undefined {
  const idx = getSearchMenuIndex(onSearchInfo);
  if (idx === undefined) return undefined;
  return onSearchInfo.componentRefs?.[idx]?.current as
    | { onInsert?: () => unknown }
    | undefined;
}

function getActiveSearchMenuCode(
  onSearchInfo: onSearchInfo
): string | undefined {
  const { index, menuData } = onSearchInfo;
  if (typeof index === "number") {
    const menuCode = menuData?.[index]?.cd_menu;
    if (menuCode) return menuCode;
  }
  return globalMenu.getState().activeMenuInfo?.cd_menu || undefined;
}

/**
 * 활성 화면의 엑셀 업로드를 연다 — 툴바 Excel > 업로드 와 같은 길(onExcelUpload). 화면이 돌려준 글은 툴바처럼 아래 알림줄에.
 * 화면에 엑셀 업로드가 없으면 false. 팝업이 닫힐 때 끝난다 (챗봇 표 첨부, 2026-10-28)
 */
export const runActiveScreenExcelUpload = async (): Promise<boolean> => {
  const info = onSearchStore.getState().onSearchInfo;
  const idx = getSearchMenuIndex(info);
  if (idx === undefined) return false;
  const handle = info.componentRefs?.[idx]?.current;
  if (typeof handle?.onExcelUpload !== "function") return false;
  const result = await handle.onExcelUpload();
  const cdMenu = info.menuData?.[idx]?.cd_menu;
  if (cdMenu) footerMsgStore.getState().handleFooterMsg(cdMenu, result);
  return true;
};

export const ExecuteRunner = {
  run: async (id: string) => {
    const info = onSearchStore.getState().onSearchInfo;

    if (
      info &&
      typeof info.index === "number" &&
      info.componentRefs &&
      info.menuData
    ) {
      const { index, componentRefs, menuData, isLoading, tabTitle } = info;
      const currentRef = componentRefs[index]?.current;
      const cdMenu = menuData[index]?.cd_menu;

      if (currentRef && typeof currentRef.onExecute === "function") {
        isLoading?.(cdMenu, true);
        tabTitle?.(cdMenu, true);
        await currentRef.onExecute(id);
        isLoading?.(cdMenu, false);
        tabTitle?.(cdMenu, false);
      }
    }
  },
};

export const requestControllerStore = create<requestControllerState>(
  (set, get) => ({
    controllerMap: new Map(),

    setController: (menuId, controller) => {
      const map = new Map(get().controllerMap);
      map.set(menuId, controller);
      set({ controllerMap: map });
    },

    abortController: (menuId) => {
      const map = new Map(get().controllerMap);
      const controller = map.get(menuId);
      if (controller) {
        controller.abort();
        map.delete(menuId);
        set({ controllerMap: map });
      }
    },

    removeController: (menuId) => {
      const map = new Map(get().controllerMap);
      map.delete(menuId);
      set({ controllerMap: map });
    },
  })
);

/* 메뉴별 수정 건수 — 그리드 · 입력폼이 등록하고 메뉴 닫기가 닫는 순간에만 묻는다(평소 비용 없음, 2026-10-16) */
export const menuChangeStore = create<MenuChangeState>((set, get) => ({
  counters: {},
  register: (menuId, id, count) =>
    set((state) => ({
      counters: { ...state.counters, [menuId]: { ...(state.counters[menuId] || {}), [id]: count } },
    })),
  unregister: (menuId, id) =>
    set((state) => {
      const own = { ...(state.counters[menuId] || {}) };
      delete own[id];
      return { counters: { ...state.counters, [menuId]: own } };
    }),
  changedCount: (menuId) =>
    Object.values(get().counters[menuId] || {}).reduce((sum, count) => {
      try {
        return sum + (count() || 0);
      } catch {
        return sum;
      }
    }, 0),
}));

/* 메뉴 화면 진행 창 — 메뉴 화면 덮개(ChatCopyProgress)가 읽는다 (2026-10-16 챗봇 행복사, 2026-10-22 제목 · 문구 — 회의록 전사) */
export const chatCopyProgressStore = create<ChatCopyProgressState>((set) => ({
  progress: {},
  setProgress: (menuId, done, total, options) =>
    set((state) => ({ progress: { ...state.progress, [menuId]: { done, total, ...options } } })),
  clearProgress: (menuId) =>
    set((state) => ({ progress: { ...state.progress, [menuId]: undefined } })),
}));

export const loadingTypeStore = create<loadingState>((set, get) => ({
  loadingTypeMap: new Map(),

  setLoadingType: (menuId, type) => {
    const map = new Map(get().loadingTypeMap);
    map.set(menuId, type);
    set({ loadingTypeMap: map });
  },

  getLoadingType: (menuId) => {
    return get().loadingTypeMap.get(menuId) ?? null;
  },

  clearLoadingType: (menuId) => {
    const map = new Map(get().loadingTypeMap);
    map.delete(menuId);
    set({ loadingTypeMap: map });
  },
}));

export const useMenuStore = create<MenuStore>((set, get) => ({
  menuItems: [],
  setMenuItems: (items) => set({ menuItems: items }),
  expandedMap: {},
  setExpandedMap: (map) => set({ expandedMap: map }),
  clearExpandedMap: () => set({ expandedMap: {} }),
}));

export const useSnEditorStore = create<SnEditorState>((set, get) => ({
  handlers: {},
  register: (id, handler) =>
    set((state) => ({
      handlers: { ...state.handlers, [id]: handler },
    })),
  unregister: (id) =>
    set((state) => {
      const { [id]: _, ...rest } = state.handlers;
      return { handlers: rest };
    }),
  trigger: (id) => {
    get().handlers[id]?.();
  },
}));

export const useNavigationStore = create<NavigationStore>((set) => ({
  navigate: null,
  setNavigate: (nav) => set({ navigate: nav }),
}));

// 공유 링크로 전달된 payload를 임시 보관/소모하기 위한 전역 스토어
export const useShareStore = create(
  persist<ShareStoreState>(
    (set) => ({
      pendingPayload: null,
      setPendingPayload: (payload) => set({ pendingPayload: payload }),
      clearPendingPayload: () => set({ pendingPayload: null }),
    }),
    {
      name: "share-link-payload",
    }
  )
);
